import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from '@material-ui/core';
import { getMoodleSystemCourseCatalog, getMoodleTrainingPartCourseCatalog } from 'http/moodle';

const groupKey = item => `${item.moduleCode}|${item.version}|${item.revision}`;
export const emptyLanguageSelection = { moduleCode: '', version: '', revision: '', variants: [] };
const emptySelection = emptyLanguageSelection;
const WARNING_ISSUES = ['inferred_metadata', 'forced_language_mismatch'];
const WARNING_ISSUE_MESSAGES = {
  inferred_metadata: 'Course metadata was inferred from its Moodle identity.',
  forced_language_mismatch: 'A selected Moodle course has a forced language that does not match its metadata.',
};

export const getBlockingIssues = item => (item.issues || []).filter(issue => !WARNING_ISSUES.includes(issue));

export const getWarningIssues = item => (item.issues || []).filter(issue => WARNING_ISSUES.includes(issue));

export const isSelectableCourse = item => Boolean(item.ltiReady) && getBlockingIssues(item).length === 0;

export const formatCourseVersion = item => `${item.moduleCode}-${item.version}.${item.revision}`;

export const formatLanguageCourse = item => `${formatCourseVersion(item)}-${item.language}`;

export const selectionForCourse = item => ({
  moduleCode: item.moduleCode,
  version: item.version,
  revision: item.revision,
  variants: [
    {
      language: item.language,
      moodleCourseId: item.courseId,
      moodleActivityId: item.ltiActivityId,
    },
  ],
});

export const selectionForVersion = (selected, items) => {
  const first = items[0];
  if (!first) return emptySelection;
  const nextKey = groupKey(first);
  const selectedKey = selected.moduleCode ? `${selected.moduleCode}|${selected.version}|${selected.revision}` : '';
  if (nextKey === selectedKey) return selected;
  return {
    moduleCode: first.moduleCode,
    version: first.version,
    revision: first.revision,
    variants: [],
  };
};

const normalizeSelection = value => {
  if (!value || typeof value !== 'object') {
    return emptySelection;
  }
  const variants = Array.isArray(value.variants) ? value.variants : [];
  return {
    moduleCode: String(value.moduleCode || '').trim(),
    version: String(value.version || '').trim(),
    revision: String(value.revision ?? '').trim(),
    variants: variants.map(variant => ({
      language: String(variant.language || '')
        .trim()
        .toLowerCase(),
      moodleCourseId: Number(variant.moodleCourseId),
      moodleActivityId:
        variant.moodleActivityId == null || variant.moodleActivityId === '' ? null : Number(variant.moodleActivityId),
    })),
  };
};

export const isSameLanguageSelection = (left, right) => {
  const a = normalizeSelection(left);
  const b = normalizeSelection(right);
  if (a.moduleCode !== b.moduleCode || a.version !== b.version || a.revision !== b.revision) {
    return false;
  }
  if (a.variants.length !== b.variants.length) {
    return false;
  }
  return a.variants.every((variant, index) => {
    const other = b.variants[index];
    return (
      variant.language === other.language &&
      variant.moodleCourseId === other.moodleCourseId &&
      variant.moodleActivityId === other.moodleActivityId
    );
  });
};

const LanguageMatrixField = ({
  input,
  disabled,
  courseSystemId,
  trainingPartCode,
  trainingPartId,
  allowAutoSelect,
}) => {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const previousCourseSystemId = useRef(null);
  const refreshRequestId = useRef(0);
  const selected = input.value || emptySelection;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const onChangeRef = useRef(input.onChange);
  onChangeRef.current = input.onChange;
  const normalizedModuleCode = String(trainingPartCode || courseSystemId || '').trim();
  const usesTrainingPartCatalog = Boolean(String(trainingPartCode || '').trim());

  const setSelectionIfChanged = useCallback(nextSelection => {
    if (isSameLanguageSelection(selectedRef.current, nextSelection)) {
      return;
    }
    onChangeRef.current(nextSelection);
  }, []);

  const refresh = async ({ preserveSelection = true } = {}) => {
    if (!normalizedModuleCode) return;
    const requestId = refreshRequestId.current + 1;
    refreshRequestId.current = requestId;
    setLoading(true);
    try {
      const response = usesTrainingPartCatalog
        ? await getMoodleTrainingPartCourseCatalog(normalizedModuleCode, trainingPartId)
        : await getMoodleSystemCourseCatalog(normalizedModuleCode);
      if (refreshRequestId.current !== requestId) return;
      const discovered = Array.isArray(response) ? response : [];
      setCatalog(discovered);
      const currentSelection = selectedRef.current;
      const hasVariants = Boolean((currentSelection.variants || []).length);

      if (discovered.length === 0) {
        // Keep saved links on load/refresh when catalog is empty; only clear when
        // the module code itself changed (preserveSelection=false).
        if (!preserveSelection) {
          setSelectionIfChanged(emptySelection);
        }
        return;
      }

      const selectable = discovered.filter(isSelectableCourse);
      const shouldAutoSelect = allowAutoSelect && selectable.length === 1 && (!preserveSelection || !hasVariants);
      if (shouldAutoSelect) {
        setSelectionIfChanged(selectionForCourse(selectable[0]));
      }
    } finally {
      if (refreshRequestId.current === requestId) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const normalizedKey = normalizedModuleCode.toLowerCase();
    if (!normalizedKey) {
      refreshRequestId.current += 1;
      setLoading(false);
      setCatalog([]);
      if (previousCourseSystemId.current) {
        setSelectionIfChanged(emptySelection);
      }
      previousCourseSystemId.current = '';
      return;
    }

    const previousKey = previousCourseSystemId.current;
    const changedFromExistingCourse = Boolean(previousKey) && previousKey !== normalizedKey;
    previousCourseSystemId.current = normalizedKey;
    if (changedFromExistingCourse) {
      refreshRequestId.current += 1;
      setLoading(false);
      setCatalog([]);
      setSelectionIfChanged(emptySelection);
    }
    refresh({ preserveSelection: !changedFromExistingCourse });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedModuleCode, usesTrainingPartCatalog]);

  const groups = useMemo(() => {
    const map = new Map();
    catalog.forEach(item => {
      const key = groupKey(item);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    });
    return map;
  }, [catalog]);
  const selectedKey = selected.moduleCode ? `${selected.moduleCode}|${selected.version}|${selected.revision}` : '';
  const versionEntries = useMemo(() => {
    const entries = Array.from(groups.entries());
    if (selectedKey && !groups.has(selectedKey)) {
      entries.unshift([
        selectedKey,
        [
          {
            moduleCode: selected.moduleCode,
            version: selected.version,
            revision: selected.revision,
          },
        ],
      ]);
    }
    return entries;
  }, [groups, selected.moduleCode, selected.revision, selected.version, selectedKey]);
  const rows = useMemo(() => {
    const discoveredRows = groups.get(selectedKey) || [];
    const discoveredIds = new Set(discoveredRows.map(item => Number(item.courseId)));
    const assignedRows = (selected.variants || [])
      .filter(variant => !discoveredIds.has(Number(variant.moodleCourseId)))
      .map(variant => ({
        courseId: variant.moodleCourseId,
        courseName: 'Assigned LMS course',
        moduleCode: selected.moduleCode,
        language: variant.language,
        version: selected.version,
        revision: selected.revision,
        ltiActivityId: variant.moodleActivityId,
        ltiReady: Boolean(variant.moodleActivityId),
        issues: [],
        validationLabel: 'validating…',
      }));
    return [...discoveredRows, ...assignedRows];
  }, [groups, selected, selectedKey]);

  const selectGroup = event => {
    const nextValue = event.target.value;
    if (!nextValue) {
      setSelectionIfChanged(emptySelection);
      return;
    }
    const entry = versionEntries.find(([key]) => key === nextValue);
    const items = entry ? entry[1] : [];
    const nextSelection = selectionForVersion(selected, items);
    setSelectionIfChanged(nextSelection);
  };
  const showNoCoursesMessage = Boolean(normalizedModuleCode) && !loading && catalog.length === 0;
  const toggle = item => {
    if (!isSelectableCourse(item)) return;
    const variants = selected.variants || [];
    const exists = variants.some(variant => variant.language === item.language);
    setSelectionIfChanged({
      ...selected,
      variants: exists
        ? variants.filter(variant => variant.language !== item.language)
        : [
            ...variants.filter(variant => variant.language !== item.language),
            {
              language: item.language,
              moodleCourseId: item.courseId,
              moodleActivityId: item.ltiActivityId,
            },
          ],
    });
  };

  return (
    <Box mt={4}>
      <Box display="flex" alignItems="center" mb={2}>
        <Typography variant="h6">LMS language variants</Typography>
        <Box ml={2}>
          <Button
            variant="outlined"
            onClick={() => refresh({ preserveSelection: true })}
            disabled={disabled || loading || !normalizedModuleCode}
          >
            {loading ? 'Refreshing…' : 'Refresh LMS courses'}
          </Button>
        </Box>
      </Box>
      <TextField
        select
        fullWidth
        variant="outlined"
        label="Course version"
        value={selectedKey}
        onChange={selectGroup}
        disabled={disabled || !normalizedModuleCode}
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
      >
        <MenuItem value="">None</MenuItem>
        {versionEntries.map(([key, items]) => (
          <MenuItem key={key} value={key}>
            {formatCourseVersion(items[0])}
          </MenuItem>
        ))}
      </TextField>
      {showNoCoursesMessage && (
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
          No LMS courses found for this module
        </Typography>
      )}
      <Box mt={2}>
        {rows.map(item => {
          const checked = (selected.variants || []).some(variant => variant.language === item.language);
          const invalidIssues = getBlockingIssues(item);
          const warningIssues = getWarningIssues(item);
          const validationText = invalidIssues.length
            ? item.validationLabel || invalidIssues.join(', ')
            : warningIssues.length
            ? `Warning: ${warningIssues.map(issue => WARNING_ISSUE_MESSAGES[issue] || issue).join(' ')}`
            : item.validationLabel || 'ready';
          return (
            <Box key={item.courseId} mb={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checked}
                    onChange={() => toggle(item)}
                    disabled={disabled || !isSelectableCourse(item)}
                  />
                }
                label={`${formatLanguageCourse(item)} — ${item.courseName} (#${item.courseId})`}
              />
              <Typography variant="caption" color={invalidIssues.length ? 'error' : 'textSecondary'}>
                {validationText}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

LanguageMatrixField.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.object,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  disabled: PropTypes.bool,
  courseSystemId: PropTypes.string,
  trainingPartCode: PropTypes.string,
  trainingPartId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  allowAutoSelect: PropTypes.bool,
};
LanguageMatrixField.defaultProps = {
  disabled: false,
  courseSystemId: '',
  trainingPartCode: '',
  allowAutoSelect: true,
};

export default LanguageMatrixField;
